import { useQuery } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import toast from "react-hot-toast";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import Link from "@mui/material/Link";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { Target } from "lucide-mui";
import FormattedDate from "../../../components/FormattedDate.tsx";
import { useWorkflowDataTypes } from "../../../hooks/resources.ts";
import {
  api,
  getErrorMessage,
  signalFilterKey,
  type Signal,
  type SignalBriefVerdict,
  type SignalFilters,
} from "../../../api/client.ts";
import { keys } from "../../../api/queryKeys.ts";
import { userHasGlobalScope } from "../../../auth/scopes.ts";
import { useAuth } from "../../../contexts/AuthContext.tsx";
import { useOrganization } from "../../../contexts/OrganizationContext.tsx";
import { useTenant } from "../../../contexts/TenantContext.tsx";
import { useUiPreference } from "../../../hooks/useUiPreference.ts";
import { useUrlState } from "../../../hooks/useUrlState.ts";
import { useRememberedCountryFilter } from "../../../hooks/useRememberedCountryFilter.ts";
import CountryName from "../../../components/CountryName.tsx";
import FilterBar from "../../../components/filters/FilterBar.tsx";
import LiveUpdatesPill from "../../../components/LiveUpdatesPill.tsx";
import { useLiveList } from "../../../realtime/useLiveList.ts";
import SavedViewsMenu from "../../../components/filters/SavedViewsMenu.tsx";
import type { FilterOption, FilterSpec } from "../../../components/filters/specs.ts";
import { useFilters } from "../../../components/filters/useFilters.ts";
import { useCollectionFilter } from "../../../components/prospects/useCollectionFilter.ts";
import ColumnPickerMenu, { type ColumnGroup } from "../../../components/ColumnPickerMenu.tsx";
import {
  MATCH_STRENGTH_OPTIONS,
  MATCH_STRENGTH_SCALE,
} from "../../../components/signals/MatchStrengthSelect.tsx";
import {
  CONFIDENCE_OPTIONS,
  RESEARCH_CONFIDENCE_SCALE,
} from "../../../components/signals/VerdictCondition.tsx";
import ResearchVerdictChip, { VERDICTS } from "../../../components/signals/ResearchVerdictChip.tsx";
import AgentActivity from "../../../components/AgentActivity.tsx";
import SignalResearchDrawer from "./SignalResearchDrawer.tsx";
import { REVIEW_LABELS } from "../../../components/review/rules.ts";
import SignalRecordLink, { recordRoute } from "../../../components/signals/SignalRecordLink.tsx";
import EmptyTableRow from "../../../components/table/EmptyTableRow.tsx";
import ScrollTable from "../../../components/table/ScrollTable.tsx";
import TableCard from "../../../components/table/TableCard.tsx";
import { formatDate, formatNumber } from "../../../utils/format.ts";

const PAGE_SIZE = 50;
const signalKey = (s: Signal) => s.id;
// The research agent's runs show in the verdict column (its status icon).
const RESEARCH_AGENT_TYPE = "signal_research";

// Two scores, two agents. The signal matcher's match strength (`rating`,
// 1-5; matched signals land on 3-5) says how strong a match the record is.
// The research agent's confidence (1-5) says how sure it is of its verdict,
// and only means something next to that verdict.
// The list holds matches only (non-matches never fire a workflow), so the
// strength options are the ones the workflow editor offers.
const MIN_STRENGTHS = MATCH_STRENGTH_OPTIONS.flatMap((o) => (o.value === null ? [] : [o.value]));
// Shown when the URL has no min_rating; "all" in the URL turns the filter off.
const DEFAULT_MIN_STRENGTH = 3;
const MIN_CONFIDENCES = CONFIDENCE_OPTIONS.flatMap((o) => (o.value === null ? [] : [o.value]));
// Shown when the URL has no verdict; "all" in the URL turns it off.
const DEFAULT_VERDICTS: SignalBriefVerdict[] = ["verified", "qualified"];
const VERDICT_VALUES = new Set<string>(VERDICTS.map((v) => v.value));

// The log opens on the researched ones worth a look; "all" in the URL turns it off.
// The countries a reviewer last filtered on, shared by the inbox and the
// worklist and kept until they change it. A link's `?country=` wins.
const COUNTRIES_PREFERENCE = "signals.countries";
const toPillOptions = (opts: { value: number | null; label: string }[]): FilterOption[] =>
  opts.flatMap((o) => (o.value === null ? [] : [{ value: String(o.value), label: o.label }]));
const STRENGTH_PILL_OPTIONS = toPillOptions(MATCH_STRENGTH_OPTIONS);
const CONFIDENCE_PILL_OPTIONS = toPillOptions(CONFIDENCE_OPTIONS);
const VERDICT_PILL_OPTIONS: FilterOption[] = VERDICTS.map((v) => ({ value: v.value, label: v.label }));

type ColumnKey =
  | "source_date"
  | "evaluated"
  | "workflow"
  | "data_type"
  | "company"
  | "country"
  | "prospect"
  | "verdict"
  | "confidence"
  | "strength"
  | "reasoning"
  | "record";

// The two agents' columns are grouped under their agent's name. The list
// only holds matches (`matched: true` below), so there is no "matched" column.
const COLUMN_GROUPS: ColumnGroup<ColumnKey>[] = [
  {
    key: "signal",
    label: null,
    columns: [
      { key: "source_date", label: "Source date" },
      { key: "evaluated", label: "Evaluated" },
      { key: "workflow", label: "Workflow" },
      { key: "data_type", label: "Data type" },
      { key: "company", label: "Company" },
      { key: "country", label: "Country" },
      { key: "prospect", label: "Prospect" },
    ],
  },
  {
    key: "research",
    label: "Research agent",
    columns: [
      { key: "verdict", label: "Verdict" },
      { key: "confidence", label: "Confidence" },
    ],
  },
  {
    key: "matcher",
    label: "Signal matcher",
    columns: [
      { key: "strength", label: "Strength" },
      { key: "reasoning", label: "Reasoning" },
    ],
  },
  {
    key: "record",
    label: null,
    columns: [
      { key: "record", label: "Record" },
    ],
  },
];
const COLUMN_KEYS = new Set<string>(COLUMN_GROUPS.flatMap((g) => g.columns.map((c) => c.key)));
// The matcher's columns start hidden: the research verdict is what readers act on.
const DEFAULT_HIDDEN_COLUMNS: ColumnKey[] = ["evaluated", "strength", "reasoning"];
const HIDDEN_COLUMNS_PREFERENCE = "signals.hidden_columns";
const isColumnKeys = (value: unknown): value is ColumnKey[] =>
  Array.isArray(value) && value.every((v) => typeof v === "string" && COLUMN_KEYS.has(v));

const COLUMN_WIDTHS: Partial<Record<ColumnKey, number>> = {
  country: 70,
  prospect: 190,
  verdict: 150,
  confidence: 90,
  strength: 80,
  record: 80,
};
const COLUMN_HINTS: Partial<Record<ColumnKey, string>> = {
  country:
    "Where the signal comes from: as research reported it, else the record's or its company's.",
  prospect:
    "The company this signal is a hit on, with the decision taken on it in Prospects and how often anything prospected it.",
  confidence: RESEARCH_CONFIDENCE_SCALE,
  strength: MATCH_STRENGTH_SCALE,
};

const groupHeaderSx = {
  borderBottom: 1,
  borderBottomColor: "divider",
  color: "text.secondary",
  typography: "overline",
  py: 0.5,
};

function parseVerdicts(raw: string | null, defaults: SignalBriefVerdict[]): SignalBriefVerdict[] {
  if (raw === null) return defaults;
  if (raw === "all") return [];
  return raw.split(",").filter((v): v is SignalBriefVerdict => VERDICT_VALUES.has(v));
}

/**
 * The signal log: every record the tenant's signal workflows matched, with the
 * matcher's strength and the research agent's verdict, including the signals
 * that reached no company. Decisions on the companies live in Prospects; each
 * row says where its company stands there.
 */
function SignalLogPage() {
  const { selectedTenantId, selectedTenant, tenantPath } = useTenant();
  const { currentOrg } = useOrganization();
  const { user } = useAuth();
  // Starting research or continuing a step spends the org's budget or steers
  // its workflows: the scope that edits them.
  const canResearch = userHasGlobalScope(user, "signal_rules:write");
  const url = useUrlState();
  const { searchParams, updateParams } = url;

  // Filters, page and the open signal all live in the URL, so a link
  // reproduces the view.
  const dataTypeFilter = searchParams.get("data_type") ?? "";
  const workflowFilter = searchParams.get("workflow") ?? "";
  const listFilter = searchParams.get("collection_id") ?? "";
  const strengthParam = searchParams.get("min_rating");
  const minStrength =
    strengthParam === "all"
      ? "all"
      : (MIN_STRENGTHS.find((r) => String(r) === strengthParam) ?? DEFAULT_MIN_STRENGTH);
  const strengthFilter = minStrength === "all" ? null : minStrength;
  const verdictFilter = parseVerdicts(searchParams.get("verdict"), DEFAULT_VERDICTS);
  const confidenceParam = searchParams.get("min_confidence");
  const confidenceFilter =
    MIN_CONFIDENCES.find((c) => String(c) === confidenceParam) ?? null;
  // A new data type drops the workflow (its list depends on it).
  const onFilterChange = useCallback((next: Record<string, unknown>) => {
    if ("data_type" in next && !("workflow" in next)) next.workflow = null;
  }, []);
  const {
    countryFilter,
    countryOptions,
    loading: countriesLoading,
    filterState,
  } = useRememberedCountryFilter({
    url,
    preferenceKey: COUNTRIES_PREFERENCE,
    queryKey: keys.signal.countries(selectedTenantId),
    loadCountries: () => api.getSignalCountries(selectedTenantId),
    onChange: onFilterChange,
  });
  // Set by the performance tab's tiles, so the list holds the rows a tile counted.
  const sinceFilter = searchParams.get("since") ?? "";
  const page = Math.max(0, parseInt(searchParams.get("page") ?? "0", 10) || 0);
  const offset = page * PAGE_SIZE;
  const signalId = searchParams.get("signal");
  const filters: SignalFilters = {
    tenant_id: selectedTenantId ?? undefined,
    data_type: dataTypeFilter || undefined,
    workflow_id: workflowFilter || undefined,
    collection_id: listFilter || undefined,
    since: sinceFilter || undefined,
    matched: true,
    min_rating: strengthFilter ?? undefined,
    research_verdict: verdictFilter.length ? verdictFilter : undefined,
    min_research_confidence: confidenceFilter ?? undefined,
    country: countryFilter.length ? countryFilter : undefined,
  };
  const filterKey = signalFilterKey(filters);

  // The last signal opened stays highlighted after its drawer closes.
  const [lastOpenedId, setLastOpenedId] = useState(signalId);
  if (signalId && signalId !== lastOpenedId) setLastOpenedId(signalId);
  const highlightedId = signalId ?? lastOpenedId;

  const { data: dataTypes = [] } = useWorkflowDataTypes();

  const { data: workflows = [] } = useQuery({
    queryKey: keys.workflow.list(selectedTenantId, "signal", dataTypeFilter || null),
    queryFn: () =>
      api.getWorkflows({
        tenant_id: selectedTenantId ?? undefined,
        trigger_type: "signal",
        data_type: dataTypeFilter || undefined,
      }),
  });

  const collectionSpec = useCollectionFilter(selectedTenantId, listFilter);

  const specs = useMemo<FilterSpec[]>(
    () => [
      {
        kind: "options",
        key: "workflow",
        label: "Workflow",
        param: "workflow",
        multiple: false,
        pinned: true,
        noneLabel: "All workflows",
        options: workflows.map((w) => ({ value: w.id, label: w.name })),
        searchPlaceholder: "Search workflows",
      },
      {
        kind: "options",
        key: "country",
        label: "Country",
        param: "country",
        pinned: true,
        loading: countriesLoading,
        options: countryOptions,
        searchPlaceholder: "Search countries",
      },
      {
        kind: "options",
        key: "verdict",
        label: "Verdict",
        param: "verdict",
        pinned: true,
        default: DEFAULT_VERDICTS,
        noneValue: "all",
        noneLabel: "Any",
        options: VERDICT_PILL_OPTIONS,
      },
      {
        kind: "options",
        key: "min_rating",
        label: "Match",
        param: "min_rating",
        group: "Signal matcher",
        multiple: false,
        default: [String(DEFAULT_MIN_STRENGTH)],
        noneValue: "all",
        noneLabel: "Any match",
        options: STRENGTH_PILL_OPTIONS,
      },
      {
        kind: "options",
        key: "data_type",
        label: "Data type",
        param: "data_type",
        group: "Signal",
        multiple: false,
        noneLabel: "All data types",
        options: dataTypes.map((d) => ({ value: d.slug, label: d.label })),
      },
      {
        // Only signals with a brief pass this; the column header carries the scale.
        kind: "options",
        key: "min_confidence",
        label: "Research confidence",
        param: "min_confidence",
        group: "Research agent",
        multiple: false,
        noneLabel: "Any confidence",
        options: CONFIDENCE_PILL_OPTIONS,
      },
      // Signals a workflow step put on it; a collection's "Signals" links here.
      collectionSpec,
      // Set by links: the performance tab's tiles.
      {
        kind: "custom",
        key: "since",
        label: "Since",
        params: ["since"],
        summary: (get) => (get("since") ? formatDate(get("since")) : null),
      },
    ],
    [workflows, countriesLoading, countryOptions, dataTypes, collectionSpec],
  );

  const filterBar = useFilters(specs, {
    state: filterState,
    pageKey: "signals.list",
    scopeId: selectedTenant?.id,
    remember: true,
    // The countries are the reviewer's own preference; since / collection come
    // from links (or are a one-off look) and mean nothing on the next visit.
    notRemembered: ["country", "since", "collection_id"],
  });

  const { data: latestRows, isLoading } = useQuery({
    queryKey: keys.signal.list(filterKey, offset),
    queryFn: () => api.getSignals({ ...filters, limit: PAGE_SIZE + 1, offset }),
    meta: { live: true },
  });

  const { data: count } = useQuery({
    queryKey: keys.signal.count(filterKey),
    queryFn: () => api.countSignals(filters),
    meta: { live: true },
  });

  // Pushed live; held still on a later page or with a signal open, so a new
  // arrival can't push the open one off the page (the locate effect would
  // jump pages under the user) and J/K step through what is shown.
  const live = useLiveList({
    rows: latestRows,
    getId: signalKey,
    frozen: page > 0 || !!signalId,
    resetKey: `${filterKey}:${offset}`,
  });
  const { rows, removedIds } = live;
  const signals = rows?.slice(0, PAGE_SIZE);
  const hasMore = (latestRows?.length ?? 0) > PAGE_SIZE;

  const openOnPage = signalId ? signals?.find((s) => s.id === signalId) : undefined;

  // A linked signal that isn't on this page: ask the server where it sits
  // under the current filters, so newer signals pushing it down don't matter.
  const { data: located, error: locateError } = useQuery({
    queryKey: keys.signal.locate(signalId ?? "", filterKey),
    queryFn: () => api.locateSignal(signalId!, filters),
    enabled: !!signalId && !!signals && !openOnPage,
    retry: false,
    // The effect below toasts and drops the link.
    meta: { suppressErrorToast: true },
  });

  useEffect(() => {
    if (!located || located.signal.id !== signalId || located.index === null) return;
    const target = Math.floor(located.index / PAGE_SIZE);
    if (target !== page) updateParams({ page: target ? String(target) : null }, true);
  }, [located, signalId, page, updateParams]);

  useEffect(() => {
    if (!locateError) return;
    toast.error(getErrorMessage(locateError));
    updateParams({ signal: null }, true);
  }, [locateError, updateParams]);

  // Signals the server found but that don't match the filters still open.
  const openSignal =
    openOnPage ?? (located && located.signal.id === signalId ? located.signal : null);

  // The rows the drawer steps through, in list order: the next page's first row
  // (fetched to know there is one; opening it jumps there). Dimmed (removed)
  // rows are skipped.
  const stepRows = rows ?? [];
  const onStep = (direction: 1 | -1) => {
    const index = stepRows.findIndex((s) => s.id === signalId);
    if (index < 0) return;
    for (let i = index + direction; i >= 0 && i < stepRows.length; i += direction) {
      if (removedIds.has(stepRows[i].id)) continue;
      updateParams({ signal: stepRows[i].id }, true);
      return;
    }
  };

  // Bring a linked or jumped-to row into view once; rows opened by a click
  // are already on screen.
  const scrolledFor = useRef<string | null>(null);
  const scrollToRow = useCallback(
    (el: HTMLTableRowElement | null) => {
      if (!el || !signalId || scrolledFor.current === signalId) return;
      scrolledFor.current = signalId;
      el.scrollIntoView({ block: "center" });
    },
    [signalId],
  );

  const [hiddenColumns, setHiddenColumns] = useUiPreference<ColumnKey[]>(
    HIDDEN_COLUMNS_PREFERENCE,
    DEFAULT_HIDDEN_COLUMNS,
    isColumnKeys,
  );
  const visibleGroups = COLUMN_GROUPS.map((g) => ({
    ...g,
    columns: g.columns.filter((c) => !hiddenColumns.includes(c.key)),
  })).filter((g) => g.columns.length > 0);
  const visibleColumns = visibleGroups.flatMap((g) => g.columns);
  const hasGroupRow = visibleGroups.some((g) => g.label);

  const dataTypeLabel = (slug: string) =>
    dataTypes.find((d) => d.slug === slug)?.label ?? slug;

  const colSpan = visibleColumns.length;

  const renderRow = (s: Signal) => {
    const cell = (key: ColumnKey) => {
      switch (key) {
        case "source_date":
          return (
            <Typography variant="caption">
              <FormattedDate value={s.source_date} fallback="—" />
            </Typography>
          );
        case "evaluated":
          return (
            <Typography variant="caption">
              <FormattedDate value={s.created_at} format="datetime" />
            </Typography>
          );
        case "workflow":
          return s.workflow_name ?? s.workflow_id;
        case "data_type":
          return <Chip label={dataTypeLabel(s.data_type)} size="small" />;
        case "company": {
          const company = s.master_company_id ? (
            <Link
              component={RouterLink}
              to={tenantPath(`/companies/${s.master_company_id}`)}
              underline="hover"
              onClick={(e) => e.stopPropagation()}
            >
              {s.master_company_name ?? s.master_company_id}
            </Link>
          ) : s.research_buyer_name ? (
            s.research_buyer_name
          ) : (
            <Typography variant="caption" sx={{ color: "text.secondary" }}>
              —
            </Typography>
          );
          const others = s.active_agent_runs.filter((r) => r.agent_type !== RESEARCH_AGENT_TYPE);
          if (!others.length) return company;
          return (
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
              {company}
              <AgentActivity runs={others} />
            </Box>
          );
        }
        case "country":
          return s.country ? (
            <CountryName code={s.country} showName={false} variant="body2" />
          ) : (
            <Typography variant="caption" sx={{ color: "text.secondary" }}>
              —
            </Typography>
          );
        case "prospect":
          return s.prospect_id ? (
            <Chip
              component={RouterLink}
              to={tenantPath(`/prospects/inbox?show=all&prospect=${s.prospect_id}`)}
              onClick={(e) => e.stopPropagation()}
              clickable
              size="small"
              variant="outlined"
              color={s.prospect_review_state === "accepted" ? "success" : "default"}
              icon={<Target />}
              label={`${s.prospect_review_state ? REVIEW_LABELS[s.prospect_review_state] : "Prospect"} · ${
                s.prospect_hit_count ?? 1
              } ${s.prospect_hit_count === 1 ? "hit" : "hits"}`}
            />
          ) : (
            <Typography variant="caption" sx={{ color: "text.secondary" }}>
              —
            </Typography>
          );
        case "verdict":
          return (
            <ResearchVerdictChip
              verdict={s.research_verdict}
              status={s.research_status}
              confidence={s.research_confidence}
            />
          );
        case "confidence":
          return s.research_verdict && s.research_confidence != null
            ? `${s.research_confidence}/5`
            : "—";
        case "strength":
          return s.rating != null ? `${s.rating}/5` : "—";
        case "reasoning":
          return (
            <Tooltip title={s.reasoning ?? ""}>
              <Typography
                variant="body2"
                sx={{
                  color: "text.secondary",
                  display: "-webkit-box",
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: "vertical",
                  overflow: "hidden",
                  maxWidth: 480,
                }}
              >
                {s.reasoning}
              </Typography>
            </Tooltip>
          );
        case "record":
          return <SignalRecordLink signal={s} onClick={(e) => e.stopPropagation()} />;
      }
    };
    const removed = removedIds.has(s.id);
    return (
      <TableRow
        key={s.id}
        ref={s.id === signalId ? scrollToRow : undefined}
        hover
        selected={s.id === highlightedId}
        onClick={() => {
          scrolledFor.current = s.id;
          updateParams({ signal: s.id });
        }}
        aria-disabled={removed || undefined}
        sx={{
          cursor: "pointer",
          ...(removed && { opacity: 0.5 }),
          // A row waiting on a person carries a warning bar; the open row's wins.
          ...(s.awaiting_approval && {
            "& > td:first-of-type": {
              boxShadow: (theme) => `inset 3px 0 0 ${theme.palette.warning.main}`,
            },
          }),
          ...(s.id === highlightedId && {
            "&.Mui-selected > td:first-of-type": {
              boxShadow: (theme) => `inset 3px 0 0 ${theme.palette.primary.main}`,
            },
          }),
        }}
      >
        {visibleColumns.map((c) => (
          <TableCell key={c.key}>{cell(c.key)}</TableCell>
        ))}
      </TableRow>
    );
  };

  return (
    <>
      <TableCard
        loading={isLoading}
        notice={<LiveUpdatesPill pendingNew={live.pendingNew} pendingRemoved={live.pendingRemoved} onApply={live.apply} />}
        toolbar={
          <FilterBar
            filters={filterBar}
            count={count ? `${formatNumber(count.total)} ${count.total === 1 ? "signal" : "signals"}` : undefined}
            views={
              <SavedViewsMenu
                filters={filterBar}
                context={{
                  tenantId: selectedTenant?.id,
                  tenantName: selectedTenant?.name,
                  organizationId: currentOrg?.id,
                  organizationName: currentOrg?.name,
                }}
              />
            }
            actions={
              <ColumnPickerMenu
                groups={COLUMN_GROUPS}
                hidden={hiddenColumns}
                onChange={setHiddenColumns}
                defaultHidden={DEFAULT_HIDDEN_COLUMNS}
              />
            }
          />
        }
        footer={
          signals && (
            // Offset paging in `page` like useUrlPaging, at a fixed page size. One
            // row past the page says whether there is a next one before the count lands.
            <TablePagination
              component="div"
              count={
                hasMore
                  ? // -1 ("of more than N") until the count lands.
                    count
                    ? Math.max(count.total, offset + PAGE_SIZE + 1)
                    : -1
                  : // A page emptied by decisions keeps `page` in range.
                    offset + Math.max(signals.length, page > 0 ? 1 : 0)
              }
              page={page}
              onPageChange={(_, next) => updateParams({ page: next ? String(next) : null })}
              rowsPerPage={PAGE_SIZE}
              rowsPerPageOptions={[]}
              sx={{ borderTop: 1, borderColor: "divider" }}
            />
          )
        }
      >
        <ScrollTable>
          <TableHead>
            {hasGroupRow && (
              <TableRow>
                {visibleGroups.map((g) =>
                  g.label ? (
                    <TableCell
                      key={g.key}
                      colSpan={g.columns.length}
                      align="center"
                      sx={groupHeaderSx}
                    >
                      {g.label}
                    </TableCell>
                  ) : (
                    g.columns.map((c) => (
                      <TableCell key={c.key} rowSpan={2} sx={{ width: COLUMN_WIDTHS[c.key] }}>
                        {c.label}
                      </TableCell>
                    ))
                  ),
                )}
              </TableRow>
            )}
            <TableRow>
              {visibleGroups
                .filter((g) => g.label || !hasGroupRow)
                .flatMap((g) => g.columns)
                .map((c) => (
                  <TableCell key={c.key} sx={{ width: COLUMN_WIDTHS[c.key] }}>
                    {COLUMN_HINTS[c.key] ? (
                      <Tooltip title={COLUMN_HINTS[c.key]}>
                        <span>{c.label}</span>
                      </Tooltip>
                    ) : (
                      c.label
                    )}
                  </TableCell>
                ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {signals?.length === 0 && (
              <EmptyTableRow colSpan={colSpan} align="left">
                {filterBar.activeCount ? "No signals match your filters" : "No signals yet"}
              </EmptyTableRow>
            )}
            {signals?.map((s) => renderRow(s))}
          </TableBody>
        </ScrollTable>
      </TableCard>

      <SignalResearchDrawer
        signal={openSignal}
        canResearch={canResearch}
        dataTypeLabel={dataTypeLabel}
        recordLink={openSignal ? recordRoute(openSignal.data_type, openSignal.record_id) : null}
        onClose={() => updateParams({ signal: null })}
        onStep={onStep}
      />
    </>
  );
}

export default SignalLogPage;
